import React, { useEffect } from 'react';
import clsx from 'clsx';

import IconSettings from '@/assets/icons/icon-settings.svg?react';
import IconSettingsFill from '@/assets/icons/icon-settings-fill.svg?react';
import IconStyles from '@/assets/icons/icon-swatch.svg?react';
import IconStylesFill from '@/assets/icons/icon-swatch-fill.svg?react';
import IconAIBrowser from '@/assets/icons/icon-ai-browser.svg?react';
import IconAIBrowserFill from '@/assets/icons/icon-ai-browser-fill.svg?react';
import IconResourcesAdd from '@/assets/icons/icon-resources-add.svg?react';
import IconResourcesAddFill from '@/assets/icons/icon-resources-add-fill.svg?react';
import IconLayers from '@/assets/icons/icon-layers.svg?react';
import IconLayersFill from '@/assets/icons/icon-layers-fill.svg?react';
import IconLayersAdd from '@/assets/icons/icon-layer-add.svg?react';
import IconLayersAddFill from '@/assets/icons/icon-layer-add-fill.svg?react';
import IconIntegrations from '@/assets/icons/icon-integrations.svg?react';
import IconIntegrationsFill from '@/assets/icons/icon-integrations-fill.svg?react';
import { SidebarIcon } from '../SidebarIcon/SidebarIcon';
import AssetsPanel from './AssetsPanel';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectPreviewModeEnabled } from '@/store/system/selectors';
import { selectActivePanel } from '@/store/assetsPanel/selector';
import { setActivePanel } from '@/store/assetsPanel/assetsPanelSlice';
import { selectIsFeatureEnabled } from '@/store/featureFlags/selectors';
import { Skeleton } from '@/components/ui/skeleton';
import { ASSETS_PANEL } from '@builder4/shared/utils/constants';
import {
  getShouldBlockStylePanelClose,
  setPendingStylePanelAction,
  STYLE_PANEL_UNSAVED_TOAST,
} from '@/utils/stylePanelGuard';
import { showToast } from '@/store/notifications/slice';
import { fetchDomains } from '@/store/domains/thunks';
import { selectHasAttemptedFetch } from '@/store/domains/selectors';
import { selectUser, selectUserLoading } from '@/store/user/selectors';

interface SidebarProps {
  className: string;
  loading?: boolean;
  disabled?: boolean;
}

export const Sidebar: React.FC<SidebarProps> = ({ className, loading, disabled = false }) => {
  const dispatch = useAppDispatch();
  const previewMode = useAppSelector(selectPreviewModeEnabled);
  const activeItem = useAppSelector(selectActivePanel);
  const isAiBuilderFeatureEnabled = useAppSelector(selectIsFeatureEnabled('builder4-ai-builder'));
  const isAiBuilderEnabled = isAiBuilderFeatureEnabled && !!import.meta.env.VITE_ANTHROPIC_API_KEY;
  const hasAttemptedFetch = useAppSelector(selectHasAttemptedFetch);
  const user = useAppSelector(selectUser);
  const userLoading = useAppSelector(selectUserLoading);
  const isLoadingState = !!loading;

  useEffect(() => {
    // Wait for user to be loaded before fetching domains (user.subDomain is needed for lpDomain)
    if (!hasAttemptedFetch && !userLoading && user) {
      dispatch(fetchDomains());
    }
  }, [dispatch, hasAttemptedFetch, userLoading, user]);

  const isStyleEditorOpen =
    activeItem === ASSETS_PANEL.STYLES_EDIT || activeItem === ASSETS_PANEL.STYLES_NEW;

  const handleIconClick = (text: string) => {
    const nextPanel = activeItem !== text ? text : '';
    if (isStyleEditorOpen && nextPanel !== activeItem) {
      if (getShouldBlockStylePanelClose()) {
        setPendingStylePanelAction(nextPanel);
        dispatch(showToast(STYLE_PANEL_UNSAVED_TOAST));
        return;
      }
    }
    dispatch(setActivePanel(nextPanel));
  };
  const handlePanelSelection = (text: string) => {
    dispatch(setActivePanel(text));
  };

  return (
    <>
      <div
        className={clsx(
          'sidebar bg-neutral-0 relative z-10 inline-flex h-[calc(100vh-52px)] flex-col items-center gap-3 border-r border-neutral-100 px-2 py-4',
          className,
          {
            'pointer-events-none opacity-50': disabled,
          },
        )}
      >
        {!isLoadingState && (
          <>
            <div className="flex flex-col gap-1">
              <SidebarIcon
                state={activeItem === ASSETS_PANEL.ELEMENTS ? 'selected' : 'default'}
                text={ASSETS_PANEL.ELEMENTS}
                icon={IconResourcesAdd}
                iconSelected={IconResourcesAddFill}
                onClick={() => handleIconClick(ASSETS_PANEL.ELEMENTS)}
                className={''}
                data-qa-selector="sidebar-elements-button"
              />
              <SidebarIcon
                state={activeItem === ASSETS_PANEL.LAYERS ? 'selected' : 'default'}
                text={ASSETS_PANEL.LAYERS}
                icon={IconLayers}
                iconSelected={IconLayersFill}
                onClick={() => handleIconClick(ASSETS_PANEL.LAYERS)}
                className={''}
                data-qa-selector="sidebar-layers-button"
              />
              <SidebarIcon
                state={
                  activeItem === ASSETS_PANEL.SECTIONS ||
                  activeItem === ASSETS_PANEL.SECTION_NAV ||
                  activeItem === ASSETS_PANEL.SECTION_HERO ||
                  activeItem === ASSETS_PANEL.SAVED_SECTIONS
                    ? 'selected'
                    : 'default'
                }
                text="Sections"
                icon={IconLayersAdd}
                iconSelected={IconLayersAddFill}
                onClick={() => handleIconClick('Sections')}
                className={''}
                data-qa-selector="sidebar-sections-button"
              />
              <SidebarIcon
                state={
                  activeItem === ASSETS_PANEL.STYLES || activeItem === ASSETS_PANEL.STYLES_NEW
                    ? 'selected'
                    : 'default'
                }
                text={ASSETS_PANEL.STYLES}
                icon={IconStyles}
                iconSelected={IconStylesFill}
                onClick={() => handleIconClick(ASSETS_PANEL.STYLES)}
                className={''}
                data-qa-selector="sidebar-styles-button"
              />
              <SidebarIcon
                state={activeItem === ASSETS_PANEL.INTEGRATIONS ? 'selected' : 'default'}
                text={ASSETS_PANEL.INTEGRATIONS}
                icon={IconIntegrations}
                iconSelected={IconIntegrationsFill}
                onClick={() => handleIconClick(ASSETS_PANEL.INTEGRATIONS)}
                className={''}
              />
              {isAiBuilderEnabled && (
                <SidebarIcon
                  state={activeItem === ASSETS_PANEL.AI_BUILDER ? 'selected' : 'default'}
                  text={ASSETS_PANEL.AI_BUILDER}
                  icon={IconAIBrowser}
                  iconSelected={IconAIBrowserFill}
                  onClick={() => handleIconClick(ASSETS_PANEL.AI_BUILDER)}
                  className={''}
                />
              )}
              <SidebarIcon
                state={activeItem === ASSETS_PANEL.SETTINGS ? 'selected' : 'default'}
                text={ASSETS_PANEL.SETTINGS}
                icon={IconSettings}
                iconSelected={IconSettingsFill}
                onClick={() => handleIconClick(ASSETS_PANEL.SETTINGS)}
                className={''}
                data-qa-selector="sidebar-settings-button"
              />
            </div>
          </>
        )}

        {isLoadingState && (
          <>
            <div className="flex flex-1 flex-col items-center justify-start px-1">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-16 w-16 p-1">
                  <Skeleton className="h-full w-full rounded-md" />
                </div>
              ))}
            </div>
            <div className="inline-flex items-center justify-start gap-2 px-1 pb-1">
              <Skeleton className="h-8 w-16 rounded-md" />
            </div>
          </>
        )}
      </div>

      {!previewMode && !isLoadingState && !disabled && (
        <AssetsPanel activeItem={activeItem} setActiveItem={handlePanelSelection} />
      )}
    </>
  );
};
